// "Log a call" modal (Session ABT): the office records an inbound call after it ends, and can
// optionally queue an NPS survey to a matched caller. Used for BOTH create (from the dashboard or
// a customer page) and edit (from a call log list). ELE's phone provider has no call-event API,
// so this manual log is the only inbound-call record. Plain English; branded; house-rule clean.
import { useEffect, useState } from "react";
import { RecordPicker } from "./RecordPicker";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "./ModalShell";
import { api, CALL_OUTCOMES } from "../lib/api";
import type { CallOutcome, CallLogDto } from "../lib/api";
import { ApiError } from "../lib/api";

// An ISO instant to the value a <input type="datetime-local"> expects (local time, no seconds).
function isoToLocalInput(iso: string | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

// The caller-mode segmented toggle button (an existing customer vs a new/unknown caller). A full
// 4-side border, never a banned left accent stripe.
function callerModeBtn(active: boolean): string {
  return `rounded-ele border px-3 py-2 font-menu text-xs uppercase tracking-wide transition ${
    active ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
  }`;
}

export function LogCallModal({
  open,
  onClose,
  onSaved,
  // When set (opened from a customer page), the call is pre-linked to this customer and the caller
  // picker is locked. Ignored in edit mode.
  lockedCustomer,
  // When set, the modal edits this existing log instead of creating a new one.
  editing,
}: {
  open: boolean;
  onClose: () => void;
  // Session AQS, W-1: carries the newly CREATED row back, so a caller (CustomerCallLog) can offer
  // "accept this call as work" the moment logging finishes -- an edit save still calls this with no
  // argument, since correcting a past call is not itself a new piece of work to raise.
  onSaved: (created?: CallLogDto) => void;
  lockedCustomer?: { id: string; name: string } | null;
  editing?: CallLogDto | null;
}) {
  const isEdit = Boolean(editing);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string>("");
  const [unmatched, setUnmatched] = useState(false); // caller is not a customer (free text)
  const [callerName, setCallerName] = useState("");
  const [callerPhone, setCallerPhone] = useState("");
  const [calledAt, setCalledAt] = useState("");
  const [duration, setDuration] = useState("");
  const [topic, setTopic] = useState("");
  const [outcome, setOutcome] = useState<CallOutcome>("resolved");
  const [nps, setNps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    if (editing) {
      setCustomerId(editing.customerId);
      setCustomerName(editing.customerName ?? "");
      setUnmatched(!editing.customerId);
      setCallerName(editing.callerName ?? "");
      setCallerPhone(editing.callerPhone ?? "");
      setCalledAt(isoToLocalInput(editing.calledAt));
      setDuration(editing.durationMinutes != null ? String(editing.durationMinutes) : "");
      setTopic(editing.topic ?? "");
      setOutcome(editing.outcome);
      setNps(false);
    } else {
      setCustomerId(lockedCustomer?.id ?? null);
      setCustomerName(lockedCustomer?.name ?? "");
      setUnmatched(false);
      setCallerName("");
      setCallerPhone("");
      setCalledAt(isoToLocalInput(undefined));
      setDuration("");
      setTopic("");
      setOutcome("resolved");
      setNps(false);
    }
  }, [open, editing, lockedCustomer]);

  const hasCustomer = Boolean(customerId) && !unmatched;
  const canSendNps = hasCustomer; // a survey needs a matched recipient

  if (!open) return null;

  async function submit() {
    setError(null);
    if (!isEdit && !hasCustomer && callerName.trim().length === 0) {
      setError("Choose a customer or enter a caller name.");
      return;
    }
    setSaving(true);
    try {
      const common = {
        callerName: unmatched ? callerName.trim() || null : null,
        callerPhone: unmatched ? callerPhone.trim() || null : null,
        calledAt: localInputToIso(calledAt),
        durationMinutes: duration.trim() === "" ? null : Number(duration),
        topic: topic.trim() || null,
        outcome,
      };
      if (isEdit && editing) {
        await api.callLogs.update(editing.id, { ...common, customerId: unmatched ? null : customerId });
        onSaved();
      } else {
        const created = await api.callLogs.create({ ...common, customerId: unmatched ? null : customerId, nps: canSendNps && nps });
        onSaved(created);
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the call. Please try again.");
      setSaving(false);
    }
  }

  return (
    <ModalShell ariaLabel={isEdit ? "Edit call" : "Log a call"} onClose={() => !saving && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="flex max-h-[88vh] flex-col overflow-y-auto p-5">
        <h2 className="mb-1 font-heading text-xl text-ele-strong">{isEdit ? "Edit call" : "Log a call"}</h2>
        <p className="mb-4 text-sm text-ele-body">Record an inbound call after it ends, from an existing customer or a new caller.{!isEdit ? " You can optionally send a matched customer a short feedback survey afterwards." : ""}</p>

        {/* Caller: an existing customer OR a new/unknown caller (a cold call or enquiry). Both are
            FIRST-CLASS - a segmented toggle in the first window, not a faint afterthought link, so a
            cold call logs in full (name, phone, topic, outcome) with no customer record required. */}
        <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Caller</label>
        {lockedCustomer && !isEdit ? (
          <div className={`${inputCls} mb-3 opacity-80`}>{lockedCustomer.name}</div>
        ) : (
          <div className="mb-3">
            <div className="mb-2 grid grid-cols-2 gap-2" role="group" aria-label="Who called?">
              <button type="button" aria-pressed={!unmatched} onClick={() => setUnmatched(false)} className={callerModeBtn(!unmatched)}>
                Existing customer
              </button>
              <button
                type="button"
                aria-pressed={unmatched}
                onClick={() => { setUnmatched(true); setCustomerId(null); setCustomerName(""); setNps(false); }}
                className={callerModeBtn(unmatched)}
              >
                New caller / enquiry
              </button>
            </div>

            {unmatched ? (
              <div className="space-y-2">
                <input value={callerName} onChange={(e) => setCallerName(e.target.value)} placeholder="Caller name" aria-label="Caller name" className={inputCls} maxLength={200} />
                <input value={callerPhone} onChange={(e) => setCallerPhone(e.target.value)} placeholder="Phone number" aria-label="Caller phone number" inputMode="tel" className={inputCls} maxLength={50} />
                {/* Session ARM (D88): no "Save as a sales lead" box any more. The call itself is the
                    record, and an unmatched one waits on the Enquiries page; a lead is made only if
                    a site inspection is raised for the caller. */}
                <p className="text-xs text-ele-body opacity-70">
                  No customer record needed. The call is logged with the name and number you enter, and waits on the Enquiries page until you match it
                  to a customer, raise work from it or delete it.
                </p>
              </div>
            ) : hasCustomer ? (
              <div className="flex items-center justify-between gap-3">
                <div className={`${inputCls} flex-1`}>{customerName}</div>
                <button type="button" onClick={() => { setCustomerId(null); setCustomerName(""); setNps(false); }} className="font-menu text-xs tracking-wide text-ele-body">Change</button>
              </div>
            ) : (
              <RecordPicker
                kind="customer"
                value=""
                placeholder="Search customers by name"
                onChange={(id, rec) => {
                  if (!id || !rec) return;
                  setCustomerId(id);
                  setCustomerName(rec.label);
                }}
              />
            )}
          </div>
        )}

        {/* Called at + duration */}
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Called at</label>
            <input type="datetime-local" value={calledAt} onChange={(e) => setCalledAt(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Duration (mins)</label>
            <input type="number" min={0} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Optional" className={inputCls} />
          </div>
        </div>

        {/* Topic */}
        <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Topic</label>
        <textarea value={topic} onChange={(e) => setTopic(e.target.value.slice(0, 300))} rows={2} placeholder="What was the call about?" className={`${inputCls} mb-1`} />
        <p className="mb-3 text-right text-xs text-ele-body opacity-70">{topic.length}/300</p>

        {/* Outcome */}
        <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Outcome</label>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value as CallOutcome)} className={`${inputCls} mb-3`}>
          {CALL_OUTCOMES.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* Send NPS (create only) */}
        {!isEdit && (
          <label className={`mb-4 flex items-center gap-3 text-sm ${canSendNps ? "text-ele-strong" : "text-ele-body opacity-60"}`}>
            <input type="checkbox" checked={nps && canSendNps} disabled={!canSendNps} onChange={(e) => setNps(e.target.checked)} className="h-4 w-4 accent-ele-green" />
            <span>
              {/* Session AHY: the survey is drafted, not sent. The delay still governs when the
                  draft appears, so the caller has hung up before anyone can send it, but the send
                  itself is now a person's. Saying "Sends shortly after the call" would promise
                  something the platform no longer does. */}
              Draft a feedback survey after this call
              <span className="block text-xs text-ele-body">{canSendNps ? "Drafted shortly after the call, for you to check and send from Gmail." : "Available once a customer is selected."}</span>
            </span>
          </label>
        )}

        {error && <p role="alert" className="mb-2 text-sm text-ele-danger">{error}</p>}

        <div className={`mt-1 gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" onClick={onClose} disabled={saving} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={saving} className={`rounded-ele bg-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-bg disabled:opacity-50 ${MODAL_ACTION_BTN}`}>
            {saving ? "Saving..." : isEdit ? "Save changes" : "Log call"}
          </button>
        </div>
    </ModalShell>
  );
}
